/**
 * Libro diario (DISENO_CONTABILIDAD §7.4): comprobantes con filtros y el
 * detalle de cada uno en un panel lateral (`?comprobante=` en la URL, así el
 * mayor puede enlazar directo a un comprobante).
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { Drawer } from "../../components/Drawer";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import { hasPermission } from "../../lib/auth";
import { useDebounced } from "../../lib/debounce";
import { cantidad, fechaLarga, pesos } from "../../lib/format";
import { useAuth } from "../auth/useAuth";
import { fetchComprobante, fetchComprobantes } from "./api";
import {
  AvisoProvisorio,
  BarraPeriodo,
  BotonesExportar,
  SinDatos,
  usePeriodoUrl,
} from "./componentes";

const POR_PAGINA = 50;
const TIPOS: Record<string, string> = {
  A: "Apertura",
  I: "Ingreso",
  E: "Egreso",
  T: "Traspaso",
};

function DetalleComprobante({ id }: { id: number }): JSX.Element {
  const q = useQuery({ queryKey: ["contabilidad", "comprobante", id], queryFn: () => fetchComprobante(id) });
  if (q.isLoading) return <Cargando />;
  if (q.isError) return <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />;
  const c = q.data;
  if (!c) return <></>;
  const debe = c.lineas.reduce((s, l) => s + l.debe, 0);
  const haber = c.lineas.reduce((s, l) => s + l.haber, 0);
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-neutral-600">{fechaLarga(c.fecha)}</span>
        <Badge tone="slate">{TIPOS[c.tipo] ?? c.tipo}</Badge>
        {c.provisorio && <Badge tone="amber">Provisorio</Badge>}
      </div>
      <p className="text-neutral-900">{c.glosa}</p>
      <table className="w-full">
        <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Cuenta</th>
            <th className="px-2 py-1.5 text-right font-medium">Debe</th>
            <th className="px-2 py-1.5 text-right font-medium">Haber</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200">
          {c.lineas.map((l, i) => (
            <tr key={i}>
              <td className="px-2 py-1.5">
                <Link
                  to={`/contabilidad/mayor/${l.cuenta_id}`}
                  className="text-neutral-900 hover:text-brand-700 hover:underline"
                >
                  {l.nombre}
                </Link>
                <span className="block text-xs tabular-nums text-neutral-600">{l.codigo}</span>
                {l.documento && <span className="block text-xs text-neutral-600">{l.documento}</span>}
                {l.glosa && l.glosa !== c.glosa && (
                  <span className="block text-xs text-neutral-600">{l.glosa}</span>
                )}
              </td>
              <td className="px-2 py-1.5 text-right align-top tabular-nums">{l.debe ? pesos(l.debe) : "—"}</td>
              <td className="px-2 py-1.5 text-right align-top tabular-nums">{l.haber ? pesos(l.haber) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-neutral-300 font-medium">
          <tr>
            <td className="px-2 py-1.5">Totales</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{pesos(debe)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{pesos(haber)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function DiarioPage(): JSX.Element {
  const { state } = useAuth();
  const url = usePeriodoUrl();
  const [params, setParams] = useSearchParams();
  const [tipo, setTipo] = useState("");
  const [texto, setTexto] = useState("");
  const [monto, setMonto] = useState("");
  const [pagina, setPagina] = useState(0);
  const textoDebounced = useDebounced(texto, 300);
  const montoNum = monto.replace(/\D/g, "");

  const filtros = {
    ...url.filtro,
    tipo: tipo || undefined,
    q: textoDebounced || undefined,
    monto: montoNum ? Number(montoNum) : undefined,
    limit: POR_PAGINA,
    offset: pagina * POR_PAGINA,
  };
  const q = useQuery({
    queryKey: ["contabilidad", "comprobantes", filtros],
    queryFn: () => fetchComprobantes(filtros),
    placeholderData: keepPreviousData,
  });
  const data = q.data;
  const abierto = Number(params.get("comprobante")) || undefined;
  const abrir = (id: number | undefined): void => {
    const next = new URLSearchParams(params);
    if (id === undefined) next.delete("comprobante");
    else next.set("comprobante", String(id));
    setParams(next, { replace: true });
  };
  const paginas = data ? Math.max(1, Math.ceil(data.total / POR_PAGINA)) : 1;
  const filtrando = Boolean(tipo || textoDebounced || montoNum);

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="space-y-3">
        <PageHeader
          title="Libro diario"
          actions={
            data &&
            !data.periodo.vacio && <BotonesExportar recurso="comprobantes" params={url.filtro} />
          }
        />
        <BarraPeriodo periodo={data?.periodo} url={url} />
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Select
              label="Tipo"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPagina(0);
              }}
            >
              <option value="">Todos</option>
              {Object.entries(TIPOS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-72">
            <Input
              label="Buscar"
              placeholder="Glosa, proveedor o RUT"
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setPagina(0);
              }}
            />
          </div>
          <div className="w-40">
            <Input
              label="Monto exacto"
              inputMode="numeric"
              placeholder="Ej: 50000"
              value={monto}
              onChange={(e) => {
                setMonto(e.target.value);
                setPagina(0);
              }}
            />
          </div>
        </div>
        <AvisoProvisorio periodo={data?.periodo} />
      </div>

      {q.isLoading && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {data && data.periodo.vacio && <SinDatos puedeCargar={hasPermission(state, "contabilidad.cargar")} />}

      {data && !data.periodo.vacio && (
        <TableWrap label="Comprobantes del libro diario">
          <table className="w-full min-w-[720px] text-sm">
            <caption className="sr-only">Comprobantes del libro diario</caption>
            <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Fecha</th>
                <th className="px-3 py-2 text-left font-medium">Comprobante</th>
                <th className="px-3 py-2 text-left font-medium">Glosa</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-neutral-600">
                    {filtrando
                      ? "Ningún comprobante calza con la búsqueda."
                      : "Sin comprobantes en el período."}
                  </td>
                </tr>
              )}
              {data.items.map((c) => (
                <tr key={c.id} className="hover:bg-neutral-50">
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{fechaLarga(c.fecha)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => abrir(c.id)}
                      className="rounded text-brand-700 hover:text-brand-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      {c.tipo} {c.numero}
                    </button>
                    {c.provisorio && (
                      <span className="ml-2">
                        <Badge tone="amber">Provisorio</Badge>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{c.glosa}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{pesos(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-3 py-2 text-sm text-neutral-600">
            <span>{cantidad(data.total)} comprobantes</span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)}>
                Anterior
              </Button>
              <span className="tabular-nums">
                {pagina + 1} / {paginas}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagina + 1 >= paginas}
                onClick={() => setPagina(pagina + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </TableWrap>
      )}

      <Drawer open={abierto !== undefined} onClose={() => abrir(undefined)} title="Comprobante">
        {abierto !== undefined && <DetalleComprobante id={abierto} />}
      </Drawer>
    </div>
  );
}
